import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'edit-campaign' }

import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import moment from 'moment-timezone'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import { stagedSendText, repeatAudienceText } from '@/util/campaignHold'
import { Badge } from 'src/components'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import EditorSectionNav from '@/components/PaymentPages/EditorSectionNav.vue'
import { unresolvableVariableLabels, formatLabelList } from './campaign-steps/campaignVariables'
import CampaignStepAudience from './campaign-steps/CampaignStepAudience.vue'
import AudienceDrawer from './AudienceDrawer.vue'
import AudienceCustomersDrawer from './AudienceCustomersDrawer.vue'
import CampaignStepTemplate from './campaign-steps/CampaignStepTemplate.vue'
import CampaignStepPromo from './campaign-steps/CampaignStepPromo.vue'
import CampaignStepSchedule from './campaign-steps/CampaignStepSchedule.vue'
import CampaignStepReview from './campaign-steps/CampaignStepReview.vue'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const companyGroupId = group.id
const tzName = group.tz_name || 'UTC'
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`
const RECIPIENT_CAP = 200000

// page state
const activeSection = ref('general')
const loading = ref(true)
const saving = ref(false)
const campaignId = ref<number | null>(null)
const campaignStatus = ref('draft')
const savedSnapshot = ref('')

const name = ref('')
const description = ref('')
const nameMissing = ref(false)
// 'one_time' sends once; 'ongoing' runs every day against a daily condition
const campaignType = ref<'one_time' | 'ongoing'>('one_time')
const audienceMode = ref('new')
const audienceFilter = ref<Record<string, any>>({})
const savedAudienceId = ref<number | null>(null)
const saveAudience = ref(false)
const audienceName = ref('')
const templateId = ref<number | null>(null)
const selectedTemplate = ref<any>(null)
const sendMode = ref('now')
const scheduleDate = ref('')
const scheduleTime = ref('')
const endsOn = ref('')      // yyyy-MM-dd, ongoing campaigns only
// Ongoing daily condition, set on the Schedule step and merged onto the
// audience filter below: the birthday is conditionDays away, or the last visit
// is more than lapseDays old (with an optional per day cap and the dedupe switch).
const conditionType = ref<'birthday_in_days' | 'days_since_last_visit'>('birthday_in_days')
const conditionDays = ref<number>(7)
const lapseDays = ref<number>(60)
const dailyLimit = ref<number | null>(null)
const skipPreviouslyEmailed = ref(true)
const isOngoing = computed(() => campaignType.value === 'ongoing')
// Issued promo code: a promo code group with default settings plus the
// validity window in days around the birthday
const promoEnabled = ref(false)
const promoTemplateId = ref<number | null>(null)
const promoDaysBefore = ref<number>(2)
const promoDaysAfter = ref<number>(2)
const promoGroups = ref<any[]>([])
// Issued codes are named <PREFIX>-XXXXXX, prefix = group name upper cased, letters and digits only, 20 max
const promoPrefix = computed(() => {
  const g = promoGroups.value.find((t: any) => t.id === promoTemplateId.value)
  const cleaned = String(g?.name || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20)
  return cleaned || 'PROMO'
})
const promoReady = computed(() => !promoEnabled.value || (!!promoTemplateId.value && Number.isInteger(promoDaysBefore.value) && Number.isInteger(promoDaysAfter.value)))
// An ongoing campaign starts with 9:00 in the group's timezone
watch(isOngoing, (on) => {
  if (on && !scheduleTime.value) scheduleTime.value = '09:00'
})
watch([endsOn, conditionDays, conditionType, lapseDays], () => {
  if (isOngoing.value) schedulePreview()
})
const testEmail = ref('')

// Confirm and error dialogs (base-alert-dialog). askConfirm resolves true on
// confirm and false on cancel or dismiss.
const confirmDialog = ref<{ show: boolean; title: string; message: string; confirmText: string; cancelText: string; resolve: ((v: boolean) => void) | null }>({
  show: false, title: '', message: '', confirmText: 'OK', cancelText: 'Cancel', resolve: null
})
function askConfirm(opts: { title: string; message: string; confirmText: string; cancelText: string }): Promise<boolean> {
  return new Promise(resolve => {
    confirmDialog.value = { ...opts, show: true, resolve }
  })
}
function settleConfirm(value: boolean) {
  const resolve = confirmDialog.value.resolve
  confirmDialog.value = { ...confirmDialog.value, show: false, resolve: null }
  if (resolve) resolve(value)
}
const errorDialog = ref({ show: false, title: '', message: '' })
function showError(title: string, message: string) {
  errorDialog.value = { show: true, title, message }
}
const testSending = ref(false)

// reference data
const templates = ref<any[]>([])
const audiences = ref<any[]>([])
const audienceDrawerOpen = ref(false)
const editingAudience = ref<any>(null)
const audienceDeleteOpen = ref(false)
const deletingAudience = ref<any>(null)
const audienceDeleting = ref(false)
const recipientsOpen = ref(false)
const games = ref<any[]>([])

// sending health (warmup warning); null when the fetch failed or hasn't landed
const healthTotals = ref<any>(null)
// Group sending domain row (automated_email_settings), null until loaded
const emailSettings = ref<any>(null)

// preview state
const previewCount = ref<number | null>(null)
// Group ids the server clamped this caller to (null when unrestricted)
const scopedGroups = ref<number[] | null>(null)
const previewSample = ref<any[]>([])
const previewLoading = ref(false)
const previewError = ref('')
// campaign email unit price, from the preview response
const billing = ref<{ unit_price: number }>({ unit_price: 0 })
let previewTimer: ReturnType<typeof setTimeout> | null = null

// list hygiene: what kickoff will skip as undeliverable, from the preview
// response; shown on the audience step and in the review checklist
const hygiene = ref<{ suppressed_total: number; reasons: Record<string, number>; unchecked_domains?: number } | null>(null)
// ramp numbers and the recent overlap, from the preview response; shown in the
// review checklist
const sendPolicyInfo = ref<{ ramp_cutoff: number; ramp_seed: number; ramp_hold_hours: number } | null>(null)
const recentOverlap = ref<{ count: number; last_sent_at: string | null; days: number } | null>(null)
// "Check now": the preview hands unchecked domains to the send worker, then we
// poll the preview until none are left (or give up after DOMAIN_CHECK_MAX_MS)
const domainChecking = ref(false)
let domainCheckTimer: ReturnType<typeof setTimeout> | null = null
const DOMAIN_CHECK_POLL_MS = 3000
const DOMAIN_CHECK_MAX_MS = 120000

async function getAuthToken(): Promise<string> {
  const session = await Auth.currentSession()
  return (session as any).idToken.jwtToken
}

async function authConfig() {
  return { headers: { Authorization: await getAuthToken() } }
}

function apiErrorMessage(err: any, fallback: string): string {
  // New lambdas return JSON error bodies; older ones return plain strings
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed?.error) return parsed.error
    } catch {
      if (typeof data === 'string' && data.length < 200) return data
    }
  }
  return fallback
}

const headerTitle = computed(() => name.value.trim() || 'New campaign')

const STATUS_BADGE_TYPES: Record<string, string> = {
  draft: 'default',
  scheduled: 'info'
}
const statusBadgeType = computed(() => STATUS_BADGE_TYPES[campaignStatus.value] || 'default')

// The audience the campaign resolves against. Ongoing: the daily condition
// rides on top of the built or saved filter (the backend merges a saved
// audience the same way; the filter panel never emits the condition itself).
const effectiveFilter = computed<Record<string, any> | null>(() => {
  let base: Record<string, any> | null
  if (audienceMode.value === 'saved') {
    const audience = audiences.value.find(a => a.id === savedAudienceId.value)
    if (!audience) return null
    base = audience.filter_json || {}
  } else {
    base = audienceFilter.value
  }
  if (!isOngoing.value) return base
  // Both condition keys are stripped and only the active one is re-added.
  const { birthday_in_days: _b, days_since_last_visit: _v, ...rest } = base || {}
  if (conditionType.value === 'days_since_last_visit') {
    // The one time "Last visited" filter would cancel the condition out
    delete rest.last_visited
    return Number.isInteger(lapseDays.value) && lapseDays.value >= 1 ? { ...rest, days_since_last_visit: { days: lapseDays.value } } : rest
  }
  return Number.isInteger(conditionDays.value) ? { ...rest, birthday_in_days: { days: conditionDays.value } } : rest
})

const scheduledMoment = computed(() => {
  if (sendMode.value !== 'schedule' || !scheduleDate.value || !scheduleTime.value) return null
  const m = moment.tz(`${scheduleDate.value} ${scheduleTime.value}`, 'YYYY-MM-DD HH:mm', tzName)
  return m.isValid() ? m : null
})

const scheduleDisplay = computed(() => {
  if (isOngoing.value) {
    if (!scheduleTime.value) return 'every day'
    const t = moment.tz(`2000-01-01 ${scheduleTime.value}`, 'YYYY-MM-DD HH:mm', tzName)
    const base = `every day at ${formatGroupTime(t.toISOString())}`
    return endsOn.value ? `${base}, until ${formatGroupDate(endsOn.value, { dayOfWeek: false })}` : base
  }
  const m = scheduledMoment.value
  if (sendMode.value === 'now' || !m) return 'Send now'
  const iso = m.toISOString()
  return `${formatGroupDate(iso)}, ${formatGroupTime(iso)}`
})

const selectedTemplateName = computed(() => {
  const t = templates.value.find(t => t.id === templateId.value)
  return t ? t.name : ''
})

function templateContentString(): string {
  const t = selectedTemplate.value
  if (!t) return ''
  const content = t.content_json ?? t.content ?? ''
  return typeof content === 'string' ? content : JSON.stringify(content)
}

// Mirrors Gate 1 in send-email-campaign: the group needs a verified custom
// domain, and the template's from address must be on it. offthecouchmail.com
// is allowed for template activation but never for bulk sends.
function emailDomain(email: string): string {
  const value = String(email || '').trim().toLowerCase()
  const at = value.lastIndexOf('@')
  return at === -1 ? '' : value.slice(at + 1)
}

const verifiedDomain = computed(() => {
  const s = emailSettings.value
  if (!s) return ''
  const ok = Number(s.custom_domain_verified) === 1 && Number(s.custom_domain_confirmed) === 1
  return ok ? String(s.domain || '').trim().toLowerCase() : ''
})

const templateFromEmail = computed(() => {
  const t = selectedTemplate.value
  if (!t) return ''
  let settingsFrom = ''
  try {
    const content = typeof t.content_json === 'string' ? JSON.parse(t.content_json) : t.content_json
    settingsFrom = content?.settings?.from_email || ''
  } catch {
    settingsFrom = ''
  }
  return String(settingsFrom || t.from_email || '').trim()
})

// Mirrors DOMAIN_GATE_EXEMPT_GROUPS on the send-email-campaign lambda: the
// test group may send from the shared domain so campaigns can be exercised
// end to end. Keep the two lists the same.
const DOMAIN_GATE_EXEMPT_GROUPS = [40]

const domainBlockReason = computed(() => {
  if (DOMAIN_GATE_EXEMPT_GROUPS.includes(Number(companyGroupId))) return ''
  if (!emailSettings.value) return ''
  if (!verifiedDomain.value) {
    return 'Campaigns require a verified custom email domain. Verify your domain under email and messaging settings first.'
  }
  if (!selectedTemplate.value) return ''
  if (emailDomain(templateFromEmail.value) !== verifiedDomain.value) {
    return `This template sends from ${templateFromEmail.value || 'an empty address'}. Campaigns must send from your verified domain ${verifiedDomain.value}. Update the from address in the template before sending.`
  }
  return ''
})

const checklist = computed(() => {
  const items: Array<{ key: string; level: string; text: string }> = []
  const content = templateContentString()

  if (domainBlockReason.value) {
    items.push({ key: 'domain', level: 'error', text: domainBlockReason.value })
  }

  // A saved audience keeps its own "Last visited" rule on the server, where it
  // would cancel the visit condition out and every run would select nobody.
  if (savedLastVisitedConflict.value) {
    items.push({ key: 'saved-last-visited', level: 'error', text: `The saved audience "${savedLastVisitedConflict.value.name}" has a Last visited rule, which conflicts with this condition. Remove it from the audience, or build the audience here instead.` })
  }

  if (content.includes('system.unsubscribeUrl')) {
    items.push({ key: 'unsub', level: 'pass', text: 'Your email includes an unsubscribe link.' })
  } else {
    items.push({ key: 'unsub', level: 'pass', text: 'An unsubscribe footer will be added automatically.' })
  }

  // Address can arrive two ways: merge variables in text blocks, or the
  // footer component's addressDisplay setting ('company'/'group'). A street
  // address typed literally into a text block cannot be detected, which is
  // why this row warns instead of blocking.
  const hasAddress = ['{{company.address', '{{companyGroup.address', '{{company_group.address',
    '"addressDisplay":"company"', '"addressDisplay":"group"']
    .some(v => content.includes(v))
  items.push(hasAddress
    ? { key: 'address', level: 'pass', text: 'Your template includes a business address.' }
    : { key: 'address', level: 'warn', text: 'No business address found in the template. Bulk email providers expect one; consider adding it to your footer.' })

  const unresolvable = unresolvableVariableLabels(content)
  if (unresolvable.length) {
    items.push({ key: 'transactional', level: 'warn', text: `This template's ${formatLabelList(unresolvable)} variables will render empty for campaign sends.` })
  }

  // One time sends: a large list goes out in stages, and an audience mailed
  // in the last week gets a repeat warning. Both come from the preview.
  if (!isOngoing.value) {
    const deliverable = previewCount.value === null ? null : Math.max(0, previewCount.value - Number(hygiene.value?.suppressed_total || 0))
    const staged = stagedSendText(deliverable, sendPolicyInfo.value)
    if (staged) items.push({ key: 'staged', level: 'pass', text: staged })
    const repeat = repeatAudienceText(previewCount.value, recentOverlap.value, (v) => formatGroupDate(v))
    if (repeat) items.push({ key: 'repeat-audience', level: 'warn', text: repeat })
  }

  if ((isOngoing.value || sendMode.value === 'schedule') && scheduleTime.value) {
    const hour = Number(scheduleTime.value.split(':')[0])
    if (hour < 8 || hour >= 20) {
      items.push({ key: 'quiet', level: 'warn', text: 'The scheduled time falls outside typical waking hours (8:00 to 20:00 in your timezone).' })
    }
  }

  if (isOngoing.value) {
    const until = endsOn.value ? 'until the stop after date, then finishes on its own' : 'until you stop it'
    if (conditionType.value === 'days_since_last_visit') {
      const limit = dailyLimit.value ? `, at most ${dailyLimit.value.toLocaleString()} emails per day` : ', no per day limit'
      const skip = skipPreviouslyEmailed.value
        ? 'Customers emailed by an earlier campaign with this condition are skipped.'
        : 'Customers emailed by an earlier campaign with this condition are emailed again.'
      items.push({ key: 'ongoing', level: 'pass', text: `Runs every day ${until}${limit}. ${skip}` })
    } else {
      items.push({ key: 'ongoing', level: 'pass', text: `Runs every day ${until}.` })
    }
  }

  if (promoEnabled.value && !content.includes('{{promo.code}}')) {
    items.push({ key: 'promo-code', level: 'warn', text: 'The template does not show {{promo.code}}, so customers will not see their code.' })
  }
  if (!promoEnabled.value && content.includes('{{promo.')) {
    items.push({ key: 'promo-missing', level: 'warn', text: 'The template uses promo code variables but no promo code is included; they will render empty.' })
  }

  if (healthTotals.value && previewCount.value) {
    const sends30 = healthTotals.value.sends || 0
    if (sends30 < 1000 && previewCount.value > sends30 * 3) {
      items.push({ key: 'warmup', level: 'warn', text: `This audience (${previewCount.value.toLocaleString()} recipients) is much larger than your recent sending volume (${sends30.toLocaleString()} emails in the last 30 days). Mailbox providers trust gradual increases; consider starting with a smaller audience.` })
    }
  }

  if (previewCount.value !== null && previewCount.value > RECIPIENT_CAP) {
    items.push({ key: 'cap', level: 'error', text: `The audience exceeds the ${RECIPIENT_CAP.toLocaleString()} recipient limit. Narrow the filters before sending.` })
  }

  // List hygiene: what kickoff will skip as undeliverable. Wording matches the
  // reason labels on the campaign report.
  if (hygiene.value) {
    const h = hygiene.value
    if (h.suppressed_total > 0) {
      const parts: string[] = []
      const labels: Record<string, string> = {
        suppressed_prior_bounce: 'previously bounced',
        suppressed_complaint: 'previously marked as spam',
        suppressed_invalid_address: 'invalid',
        suppressed_disposable_domain: 'disposable',
        suppressed_no_mx: 'on a domain that cannot receive mail'
      }
      for (const [code, label] of Object.entries(labels)) {
        const n = h.reasons[code] || 0
        if (n > 0) parts.push(`${n.toLocaleString()} ${label}`)
      }
      items.push({ key: 'hygiene', level: 'warn', text: `${h.suppressed_total.toLocaleString()} ${h.suppressed_total === 1 ? 'address' : 'addresses'} will be skipped as undeliverable (${parts.join(', ')}). Each one stays in the campaign report with its reason.` })
    } else {
      items.push({ key: 'hygiene', level: 'pass', text: 'No known undeliverable addresses in this audience.' })
    }
  }

  return items
})

const overCap = computed(() => previewCount.value !== null && previewCount.value > RECIPIENT_CAP)

// Estimated campaign cost, empty until both a count and a unit price are known.
// Platform billing is always in USD, so this shows "$" regardless of the
// group's display currency (the invoice amount is what it must match).
const estimatedCost = computed(() => {
  if (!billing.value.unit_price || !previewCount.value) return ''
  // Round to cents BEFORE formatting (exponential pattern per CLAUDE.md):
  // count x 0.0025 yields fractional cents like 30.8625.
  const cents = Math.round(Number(previewCount.value * billing.value.unit_price + 'e2'))
  return '$' + (cents / 100).toFixed(2)
})

// ── section readiness (drives nav warn dots + the send button) ──────────────

// A saved audience keeps its own "Last visited" rule on the server, where it
// would cancel the visit condition out and every run would select nobody.
const savedLastVisitedConflict = computed(() => {
  if (!isOngoing.value || conditionType.value !== 'days_since_last_visit' || audienceMode.value !== 'saved') return null
  const audience = audiences.value.find(a => a.id === savedAudienceId.value)
  return audience?.filter_json?.last_visited ? audience : null
})
const hasDailyCondition = computed(() => conditionType.value === 'days_since_last_visit'
  ? Number.isInteger(lapseDays.value) && lapseDays.value >= 1
  : Number.isInteger(conditionDays.value))
const audienceReady = computed(() => {
  if (audienceMode.value === 'saved' && !savedAudienceId.value) return false
  // An ongoing campaign needs its daily condition; today's count may be zero
  // (nobody has a birthday N days from today) and that is fine.
  if (isOngoing.value) return hasDailyCondition.value && !savedLastVisitedConflict.value && !previewError.value && !overCap.value
  return !previewError.value && previewCount.value !== null && previewCount.value > 0 && !overCap.value
})
const templateReady = computed(() => !!templateId.value && !domainBlockReason.value && promoReady.value)
const scheduleReady = computed(() =>
  isOngoing.value ? (!!scheduleTime.value && hasDailyCondition.value) : (sendMode.value === 'now' || !!(scheduledMoment.value && scheduledMoment.value.isAfter(moment()))))
const nameReady = computed(() => !!name.value.trim())
const allReady = computed(() =>
  audienceReady.value && templateReady.value && scheduleReady.value && nameReady.value && !saving.value)

const readinessHint = computed(() => {
  if (isOngoing.value && !hasDailyCondition.value) {
    return conditionType.value === 'days_since_last_visit' ? 'Set the days without a visit on the Send section' : 'Set the days before the birthday on the Send section'
  }
  if (savedLastVisitedConflict.value) return 'The saved audience has a Last visited rule that conflicts with this condition'
  if (!audienceReady.value) return 'Choose an audience with at least one recipient'
  if (!templateId.value) return 'Pick an email template'
  if (domainBlockReason.value) return 'The template must send from your verified domain'
  if (!promoReady.value) return 'Pick a promo code group or turn the promo code off'
  if (!scheduleReady.value) return isOngoing.value ? 'Pick the daily send time' : 'Pick a future date and time'
  if (!nameReady.value) return 'Name the campaign on the general section'
  return ''
})

// The footer's primary action progresses through the sections; only the last
// section carries the send button, so no stage dead-ends on a disabled action
const nextSection = computed(() => {
  const order = navSections.value
  const idx = order.findIndex(s => s.id === activeSection.value)
  return idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null
})

async function onCancel() {
  if (isDirty.value) {
    const ok = await askConfirm({
      title: 'Discard unsaved changes?',
      message: 'Your changes to this campaign will be lost.',
      confirmText: 'Discard',
      cancelText: 'Keep editing'
    })
    if (!ok) return
  }
  goToList()
}

const navSections = computed(() => [
  { id: 'general', label: 'General', warn: !audienceReady.value || !nameReady.value, warnText: !nameReady.value ? 'Name needed' : isOngoing.value && !hasDailyCondition.value ? 'Condition needed' : 'Audience needed' },
  { id: 'template', label: 'Template', warn: !templateReady.value, warnText: templateId.value && domainBlockReason.value ? 'From address not on verified domain' : !promoReady.value ? 'Promo code group needed' : 'Template needed' },
  { id: 'send', label: 'Send', warn: !scheduleReady.value || overCap.value, warnText: overCap.value ? 'Audience over the limit' : 'Schedule needed' }
])

const audienceChips = computed(() => {
  const filter = effectiveFilter.value || {}
  const chips: string[] = []
  if (isOngoing.value) chips.push('Ongoing')
  if (audienceMode.value === 'saved') {
    const audience = audiences.value.find(a => a.id === savedAudienceId.value)
    if (audience) chips.push(`Saved audience: ${audience.name}`)
  }
  if (filter.first_name) chips.push(`First name contains "${filter.first_name.contains}"`)
  if (filter.last_name) chips.push(`Last name contains "${filter.last_name.contains}"`)
  if (filter.email) chips.push(`Email contains "${filter.email.contains}"`)
  if (filter.games) chips.push(`Played ${filter.games.ids.length} selected event${filter.games.ids.length > 1 ? 's' : ''}`)
  if (filter.tags) chips.push(`Tags ${filter.tags.mode}: ${filter.tags.names.join(', ')}`)
  if (filter.last_visited) {
    chips.push(filter.last_visited.op === 'never' ? 'Never visited'
      : filter.last_visited.op === 'within_days' ? `Visited within ${filter.last_visited.days} days`
      : `Last visit over ${filter.last_visited.days} days ago`)
  }
  if (filter.lifetime_spend) {
    const gs = getGroupSettings()
    const amount = formatCurrency(filter.lifetime_spend.amount, {
      currency: gs.currency,
      currencyFormat: gs.currency_format,
      numberFormat: gs.number_format
    })
    chips.push(`Spend ${filter.lifetime_spend.op === 'gte' ? 'at least' : 'at most'} ${amount}`)
  }
  if (filter.birthday_month) chips.push('Birthday this month')
  if (filter.birthday_in_days) chips.push(`Birthday in ${filter.birthday_in_days.days} days`)
  if (filter.days_since_last_visit) chips.push(`Has not visited in ${filter.days_since_last_visit.days} days`)
  return chips
})

// ── dirty tracking (footer message) ─────────────────────────────────────────

function currentSnapshot(): string {
  return JSON.stringify({
    name: name.value,
    description: description.value,
    templateId: templateId.value,
    campaignType: campaignType.value,
    audienceMode: audienceMode.value,
    audienceFilter: audienceFilter.value,
    savedAudienceId: savedAudienceId.value,
    sendMode: sendMode.value,
    scheduleDate: scheduleDate.value,
    scheduleTime: scheduleTime.value,
    endsOn: endsOn.value,
    conditionDays: conditionDays.value,
    conditionType: conditionType.value,
    lapseDays: lapseDays.value,
    dailyLimit: dailyLimit.value,
    skipPreviouslyEmailed: skipPreviouslyEmailed.value,
    promoEnabled: promoEnabled.value,
    promoTemplateId: promoTemplateId.value,
    promoDaysBefore: promoDaysBefore.value,
    promoDaysAfter: promoDaysAfter.value
  })
}

const isDirty = computed(() => currentSnapshot() !== savedSnapshot.value)

// ── preview ─────────────────────────────────────────────────────────────────

function schedulePreview() {
  if (previewTimer) clearTimeout(previewTimer)
  previewTimer = setTimeout(runPreview, 500)
}

function stopDomainCheck() {
  domainChecking.value = false
  if (domainCheckTimer) clearTimeout(domainCheckTimer)
  domainCheckTimer = null
}

async function runDomainCheck() {
  const filter = effectiveFilter.value
  if (filter === null || domainChecking.value) return
  domainChecking.value = true
  const startedAt = Date.now()
  try {
    const config = await authConfig()
    const response = await axios.post(`${baseUrl}/email-campaigns/audience-preview`,
      { audience_filter: filter, resolve_domains: true, ...previewScope() }, config)
    hygiene.value = response.data.hygiene || hygiene.value
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: apiErrorMessage(err, 'Could not start the domain check'), icon: 'tim-icons icon-alert-circle-exc' })
    stopDomainCheck()
    return
  }
  const poll = async () => {
    if (!domainChecking.value) return
    await runPreview()
    const left = hygiene.value?.unchecked_domains || 0
    if (left === 0 || Date.now() - startedAt > DOMAIN_CHECK_MAX_MS) {
      stopDomainCheck()
      if (left > 0) proxy.$notify({ type: 'warning', message: 'The domain check is still running. Reopen the audience in a minute to see the final count.', icon: 'tim-icons icon-alert-circle-exc' })
      return
    }
    domainCheckTimer = setTimeout(poll, DOMAIN_CHECK_POLL_MS)
  }
  domainCheckTimer = setTimeout(poll, DOMAIN_CHECK_POLL_MS)
}

// Ongoing campaigns are estimated as their reach (everyone who will be
// emailed between today and the stop after date, or ever when there is none),
// not today's slice; the daily run itself always uses today's slice.
function previewScope(): Record<string, any> {
  return isOngoing.value ? { estimate_reach: true, ends_on: endsOn.value || null } : {}
}

async function runPreview() {
  const filter = effectiveFilter.value
  if (filter === null) {
    previewCount.value = null
    previewSample.value = []
    hygiene.value = null
    sendPolicyInfo.value = null
    recentOverlap.value = null
    return
  }
  previewLoading.value = true
  previewError.value = ''
  try {
    const config = await authConfig()
    const response = await axios.post(`${baseUrl}/email-campaigns/audience-preview`,
      { audience_filter: filter, ...previewScope() }, config)
    previewCount.value = response.data.count
    previewSample.value = response.data.sample || []
    billing.value = response.data.billing || { unit_price: 0 }
    hygiene.value = response.data.hygiene || null
    sendPolicyInfo.value = response.data.send_policy || null
    recentOverlap.value = response.data.recent_overlap || null
    // Restricted callers are clamped server side on preview and on save, so
    // an empty Groups select simply means any of their groups; the hint says
    // so and the chips stay as the user left them.
    scopedGroups.value = response.data.scoped_groups || null
  } catch (err: any) {
    previewError.value = apiErrorMessage(err, 'Could not calculate the audience size')
    previewCount.value = null
    previewSample.value = []
    hygiene.value = null
    sendPolicyInfo.value = null
    recentOverlap.value = null
  } finally {
    previewLoading.value = false
  }
}

function onFilterChange(filter: Record<string, any>) {
  audienceFilter.value = filter
  schedulePreview()
}

function onAudienceModeChange(mode: string) {
  audienceMode.value = mode
  schedulePreview()
}

// The filter panel drops the other type's keys itself; the audience step
// re-applies the daily condition when the type turns ongoing.
function onCampaignTypeChange(type: string) {
  campaignType.value = type === 'ongoing' ? 'ongoing' : 'one_time'
  schedulePreview()
}

function onConditionTypeChange(type: string) {
  conditionType.value = type === 'days_since_last_visit' ? 'days_since_last_visit' : 'birthday_in_days'
  // A visit lapse code is valid from the send day; there is no date ahead to count back from
  if (conditionType.value === 'days_since_last_visit') promoDaysBefore.value = 0
  schedulePreview()
}

function onSavedAudienceChange(id: number) {
  savedAudienceId.value = id
  schedulePreview()
}

// ── saved audience management ───────────────────────────────────────────────

function openAudienceEditor(audience: any) {
  editingAudience.value = audience
  audienceDrawerOpen.value = true
}

function onAudienceSaved(updated: any) {
  audiences.value = audiences.value.map(a => (a.id === updated.id ? updated : a))
  if (savedAudienceId.value === updated.id) schedulePreview()
}

function promptDeleteAudience(audience: any) {
  deletingAudience.value = audience
  audienceDeleteOpen.value = true
}

async function deleteAudience() {
  const audience = deletingAudience.value
  if (!audience || audienceDeleting.value) return
  audienceDeleting.value = true
  try {
    const config = await authConfig()
    await axios.delete(`${baseUrl}/customer-audiences/${audience.id}`, config)
    audiences.value = audiences.value.filter(a => a.id !== audience.id)
    if (savedAudienceId.value === audience.id) {
      savedAudienceId.value = null
      schedulePreview()
    }
    proxy.$notify({ type: 'success', message: 'Audience deleted', icon: 'tim-icons icon-check-2' })
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: apiErrorMessage(err, 'The audience could not be deleted'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    audienceDeleting.value = false
    audienceDeleteOpen.value = false
    deletingAudience.value = null
  }
}

// ── template ────────────────────────────────────────────────────────────────

async function onTemplateChange(id: number) {
  templateId.value = id
  selectedTemplate.value = null
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/email-templates/${id}`, config)
    selectedTemplate.value = response.data.template || null
  } catch (err) {
    // Non-blocking: warnings simply won't show
    selectedTemplate.value = null
  }
}

function goToTemplatesTab() {
  proxy.$router.push('/mail/custom/landing?tab=0')
}

// ── persistence ─────────────────────────────────────────────────────────────

function buildPayload() {
  const payload: Record<string, any> = {
    name: name.value.trim(),
    description: description.value.trim() || null,
    template_id: templateId.value || null,
    audience_filter: effectiveFilter.value ?? {}
  }
  payload.campaign_type = campaignType.value
  payload.promo_template_id = promoEnabled.value ? promoTemplateId.value : null
  payload.promo_valid_days_before = promoDaysBefore.value
  payload.promo_valid_days_after = promoDaysAfter.value
  if (audienceMode.value === 'saved' && savedAudienceId.value) {
    payload.audience_id = savedAudienceId.value
  }
  if (isOngoing.value) {
    // The backend computes the next run from the daily time in the group's timezone
    payload.send_time = scheduleTime.value || '09:00'
    payload.daily_limit = conditionType.value === 'days_since_last_visit' ? dailyLimit.value : null
    payload.skip_previously_emailed = conditionType.value === 'days_since_last_visit' ? skipPreviouslyEmailed.value : true
    payload.ends_on = endsOn.value || null
  } else if (sendMode.value === 'schedule' && scheduledMoment.value) {
    payload.scheduled_at = scheduledMoment.value.toISOString()
  }
  return payload
}

async function persistCampaign(): Promise<boolean> {
  const config = await authConfig()

  // Save the audience preset first so the campaign can reference it
  if (audienceMode.value === 'new' && saveAudience.value && audienceName.value.trim()) {
    try {
      const response = await axios.post(`${baseUrl}/customer-audiences`,
        { name: audienceName.value.trim(), filter_json: audienceFilter.value }, config)
      audiences.value = [{ id: response.data.id, name: audienceName.value.trim(), filter_json: response.data.filter_json || audienceFilter.value }, ...audiences.value]
      saveAudience.value = false
    } catch (err: any) {
      proxy.$notify({ type: 'warning', message: apiErrorMessage(err, 'Could not save the audience preset'), icon: 'tim-icons icon-alert-circle-exc' })
      // Not fatal for the campaign itself
    }
  }

  const payload = buildPayload()
  try {
    if (campaignId.value) {
      const response = await axios.put(`${baseUrl}/email-campaigns/${campaignId.value}`, payload, config)
      campaignStatus.value = response.data.status || campaignStatus.value
    } else {
      const response = await axios.post(`${baseUrl}/email-campaigns`, payload, config)
      campaignId.value = response.data.id
      campaignStatus.value = response.data.status || 'draft'
    }
    savedSnapshot.value = currentSnapshot()
    return true
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: apiErrorMessage(err, 'Could not save the campaign'), icon: 'tim-icons icon-alert-circle-exc' })
    return false
  }
}

async function saveDraft() {
  saving.value = true
  try {
    const saved = await persistCampaign()
    if (saved) {
      proxy.$notify({ type: 'success', message: 'Draft saved', icon: 'tim-icons icon-check-2' })
    }
  } finally {
    saving.value = false
  }
}

async function confirmAndSend() {
  if (!name.value.trim()) {
    nameMissing.value = true
    activeSection.value = 'general'
    return
  }

  const isSchedule = sendMode.value === 'schedule'
  const countLabel = previewCount.value === null ? 'your audience' : `approximately ${previewCount.value.toLocaleString()} recipients`
  const ok = await askConfirm({
    title: isOngoing.value ? 'Start this ongoing campaign?' : isSchedule ? 'Schedule this campaign?' : 'Send this campaign?',
    message: isOngoing.value
      ? `It will send ${scheduleDisplay.value} to the customers who match the condition that day${endsOn.value ? '.' : ', until you stop it.'}`
      : isSchedule
        ? `It will send to ${countLabel} at ${scheduleDisplay.value}.`
        : `It will start sending to ${countLabel} right away.`,
    confirmText: isOngoing.value ? 'Start' : isSchedule ? 'Schedule' : 'Send now',
    cancelText: 'Not yet'
  })
  if (!ok) return

  saving.value = true
  try {
    const saved = await persistCampaign()
    if (!saved) return

    if (isOngoing.value) {
      proxy.$notify({ type: 'success', message: 'Ongoing campaign started', icon: 'tim-icons icon-check-2' })
      goToList()
      return
    }

    if (isSchedule) {
      proxy.$notify({ type: 'success', message: 'Campaign scheduled', icon: 'tim-icons icon-check-2' })
      goToList()
      return
    }

    const config = await authConfig()
    try {
      const sendResponse = await axios.post(`${baseUrl}/email-campaigns/${campaignId.value}/send`, {}, config)
      if (sendResponse.data?.held === true) {
        proxy.$notify({ type: 'warning', message: sendResponse.data.message || 'Sending is held while platform email reputation recovers. It starts on its own.', icon: 'tim-icons icon-alert-circle-exc', timeout: 12000 })
      } else {
        proxy.$notify({ type: 'success', message: 'Campaign is sending', icon: 'tim-icons icon-send' })
      }
      goToList()
    } catch (err: any) {
      showError('Campaign not sent', apiErrorMessage(err, 'The campaign could not be sent'))
    }
  } finally {
    saving.value = false
  }
}

async function sendTest() {
  if (!templateId.value || !testEmail.value.trim()) return
  testSending.value = true
  try {
    const config = await authConfig()
    await axios.post(`${baseUrl}/email-templates/${templateId.value}/send-test`,
      { recipientEmail: testEmail.value.trim(), campaignPreview: true, promoPrefix: promoEnabled.value ? promoPrefix.value : undefined }, config)
    proxy.$notify({ type: 'success', message: 'Test email sent', icon: 'tim-icons icon-check-2' })
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: apiErrorMessage(err, 'Could not send the test email'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    testSending.value = false
  }
}

// ── load ────────────────────────────────────────────────────────────────────

function seedFromCampaign(campaign: any) {
  name.value = campaign.name === 'Untitled campaign' ? '' : (campaign.name || '')
  description.value = campaign.description || ''
  templateId.value = campaign.template_id || null
  if (templateId.value) onTemplateChange(templateId.value)
  audienceFilter.value = campaign.audience_filter || {}
  if (campaign.audience_id) {
    audienceMode.value = 'saved'
    savedAudienceId.value = campaign.audience_id
  }
  promoEnabled.value = !!campaign.promo_template_id
  promoTemplateId.value = campaign.promo_template_id || null
  promoDaysBefore.value = Number.isInteger(Number(campaign.promo_valid_days_before)) ? Number(campaign.promo_valid_days_before) : 2
  promoDaysAfter.value = Number.isInteger(Number(campaign.promo_valid_days_after)) ? Number(campaign.promo_valid_days_after) : 2
  if (campaign.recurrence === 'daily') {
    campaignType.value = 'ongoing'
    const visitDays = campaign.audience_filter?.days_since_last_visit?.days
    if (Number.isInteger(visitDays)) {
      conditionType.value = 'days_since_last_visit'
      lapseDays.value = visitDays
      dailyLimit.value = campaign.daily_limit === null || campaign.daily_limit === undefined ? null : Number(campaign.daily_limit)
      skipPreviouslyEmailed.value = Number(campaign.skip_previously_emailed ?? 1) === 1
    } else {
      conditionType.value = 'birthday_in_days'
    }
    const savedDays = campaign.audience_filter?.birthday_in_days?.days
    if (Number.isInteger(savedDays)) conditionDays.value = savedDays
    scheduleTime.value = String(campaign.send_time || '09:00').slice(0, 5)
    endsOn.value = campaign.ends_on ? String(campaign.ends_on).slice(0, 10) : ''
    return
  }
  if (campaign.scheduled_at) {
    const m = moment.utc(campaign.scheduled_at).tz(tzName)
    if (m.isValid() && m.isAfter(moment())) {
      sendMode.value = 'schedule'
      scheduleDate.value = m.format('YYYY-MM-DD')
      scheduleTime.value = m.format('HH:mm')
    }
  }
}

async function loadSendingHealth() {
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/email-health`, config)
    healthTotals.value = response.data.totals
  } catch {
    // silent: the warmup warning is simply not shown
  }
}

async function loadEmailSettings() {
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/email-settings`, config)
    emailSettings.value = response.data.settings || null
  } catch {
    // silent: the domain check then runs server side only, at kickoff
    emailSettings.value = null
  }
}

onMounted(async () => {
  loadSendingHealth()
  loadEmailSettings()
  try {
    const config = await authConfig()
    const routeId = proxy.$route.params.id ? Number(proxy.$route.params.id) : null
    const fromId = proxy.$route.query.from ? Number(proxy.$route.query.from) : null

    // Independent HTTP requests: fetch them together so the page waits for the
    // slowest one, not the sum. (Browser side; the one-connection rule that
    // makes Promise.all pointless inside a lambda does not apply here.)
    const seedId = routeId || fromId
    const [templatesRes, audiencesRes, gamesRes, promoGroupsRes, campaignRes] = await Promise.all([
      axios.get(`${baseUrl}/email-templates?count=1000&offset=0`, config),
      axios.get(`${baseUrl}/customer-audiences`, config),
      axios.get(`${baseUrl}/games`, config),
      axios.get(`${baseUrl}/promo-code-templates?count=200&offset=0`, config).catch(() => ({ data: { templates: [] } })),
      seedId ? axios.get(`${baseUrl}/email-campaigns/${seedId}`, config) : Promise.resolve(null)
    ])
    templates.value = templatesRes.data.templates || []
    audiences.value = audiencesRes.data.audiences || []
    games.value = gamesRes.data || []
    // Only groups with default settings can issue codes
    promoGroups.value = (promoGroupsRes.data.templates || []).filter((t: any) => Number(t.has_template) === 1)

    if (routeId && campaignRes) {
      const campaign = campaignRes.data.campaign
      if (!['draft', 'scheduled'].includes(campaign.status)) {
        proxy.$notify({ type: 'warning', message: 'This campaign can no longer be edited', icon: 'tim-icons icon-alert-circle-exc' })
        goToList()
        return
      }
      // An ongoing campaign that has started is read only: stop it and
      // duplicate it to change anything. Its report shows the runs so far.
      if (campaign.recurrence === 'daily' && campaign.status === 'scheduled') {
        proxy.$notify({ type: 'warning', message: 'An ongoing campaign that has started cannot be edited. Stop it and duplicate it to make changes.', icon: 'tim-icons icon-alert-circle-exc' })
        proxy.$router.push(`/marketing/campaigns/${campaign.id}/report`)
        return
      }
      campaignId.value = campaign.id
      campaignStatus.value = campaign.status
      seedFromCampaign(campaign)
    } else if (fromId && campaignRes) {
      // duplicate: copy name, template, and filter into a fresh draft
      seedFromCampaign(campaignRes.data.campaign)
    }

    savedSnapshot.value = currentSnapshot()
    runPreview()
  } catch (err: any) {
    if (err?.response?.status === 401) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: apiErrorMessage(err, 'Could not load campaign data'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  stopDomainCheck()
  if (previewTimer) clearTimeout(previewTimer)
})

function goToList() {
  proxy.$router.push('/marketing/campaigns')
}

function copyUtmToken() {
  navigator.clipboard.writeText(`campaign-${campaignId.value}`)
  proxy.$notify({ type: 'success', message: 'Copied to clipboard', icon: 'tim-icons icon-check-2' })
}

return { __sfc: true,proxy, companyId, group, companyGroupId, tzName, baseUrl, RECIPIENT_CAP, activeSection, loading, saving, campaignId, campaignStatus, savedSnapshot, name, description, nameMissing, campaignType, audienceMode, audienceFilter, savedAudienceId, saveAudience, audienceName, templateId, selectedTemplate, sendMode, scheduleDate, scheduleTime, endsOn, conditionType, conditionDays, lapseDays, dailyLimit, skipPreviouslyEmailed, isOngoing, promoEnabled, promoTemplateId, promoDaysBefore, promoDaysAfter, promoGroups, promoPrefix, promoReady, testEmail, confirmDialog, askConfirm, settleConfirm, errorDialog, showError, testSending, templates, audiences, audienceDrawerOpen, editingAudience, audienceDeleteOpen, deletingAudience, audienceDeleting, recipientsOpen, games, healthTotals, emailSettings, previewCount, scopedGroups, previewSample, previewLoading, previewError, billing, previewTimer, hygiene, sendPolicyInfo, recentOverlap, domainChecking, domainCheckTimer, DOMAIN_CHECK_POLL_MS, DOMAIN_CHECK_MAX_MS, getAuthToken, authConfig, apiErrorMessage, headerTitle, STATUS_BADGE_TYPES, statusBadgeType, effectiveFilter, scheduledMoment, scheduleDisplay, selectedTemplateName, templateContentString, emailDomain, verifiedDomain, templateFromEmail, DOMAIN_GATE_EXEMPT_GROUPS, domainBlockReason, checklist, overCap, estimatedCost, savedLastVisitedConflict, hasDailyCondition, audienceReady, templateReady, scheduleReady, nameReady, allReady, readinessHint, nextSection, onCancel, navSections, audienceChips, currentSnapshot, isDirty, schedulePreview, stopDomainCheck, runDomainCheck, previewScope, runPreview, onFilterChange, onAudienceModeChange, onCampaignTypeChange, onConditionTypeChange, onSavedAudienceChange, openAudienceEditor, onAudienceSaved, promptDeleteAudience, deleteAudience, onTemplateChange, goToTemplatesTab, buildPayload, persistCampaign, saveDraft, confirmAndSend, sendTest, seedFromCampaign, loadSendingHealth, loadEmailSettings, goToList, copyUtmToken, Badge, Breadcrumb, BreadcrumbItem, EditorSectionNav, CampaignStepAudience, AudienceDrawer, AudienceCustomersDrawer, CampaignStepTemplate, CampaignStepPromo, CampaignStepSchedule, CampaignStepReview }
}

})